import {useCallback, useEffect, useRef, useState} from 'react';

// Odpočet do další automatické obnovy dat.
//
// Zbývající čas se dopočítává z cílového časového razítka, neodečítá se po sekundách.
// Mobilní prohlížeč timery na pozadí throttluje nebo úplně zmrazí, takže odečítací
// varianta po probuzení pokračovala tam, kde přestala, a data zůstala stará. Tady se
// odpočet po návratu do aplikace srovná a zmeškaná obnova proběhne okamžitě.
//
// Tikání řeší samo-korigující setTimeout naplánovaný přesně na okamžik, kdy se
// zobrazené číslo mění. Pevný setInterval(1000) se proti cílovému razítku rozfázoval
// (drift, blokovaný main thread při fetchi) a odpočet pak vizuálně přeskakoval čísla.
export default function useCountdownRefresh(intervalSec, onRefresh, {active = true, pauseWhenHidden = false, refreshOnStart = false} = {}) {
	const [secondsLeft, setSecondsLeft] = useState(intervalSec);
	const deadlineAt = useRef(0);
	const onRefreshRef = useRef(onRefresh);
	const rescheduleRef = useRef(null);

	onRefreshRef.current = onRefresh;

	const restart = useCallback(() => {
		deadlineAt.current = Date.now() + intervalSec * 1000;
		setSecondsLeft(intervalSec);

		if (rescheduleRef.current) {
			rescheduleRef.current();
		}
	}, [intervalSec]);

	useEffect(() => {
		if (!active) {
			rescheduleRef.current = null;

			return undefined;
		}

		deadlineAt.current = Date.now() + intervalSec * 1000;
		setSecondsLeft(intervalSec);

		let timeoutId = null;

		// Prodleva do nejbližší hranice celé sekundy před cílem, tedy do okamžiku,
		// kdy Math.ceil níže vrátí o jedničku menší hodnotu. Drobná rezerva brání
		// tomu, aby timer vystřelil pár milisekund před hranicí a hodnota se nezměnila.
		const delayToNextTick = () => {
			const untilDeadline = deadlineAt.current - Date.now();

			if (untilDeadline <= 0) {
				return 0;
			}

			return ((untilDeadline - 1) % 1000) + 1 + 15;
		};

		const scheduleNext = (delay = delayToNextTick()) => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			timeoutId = setTimeout(tick, delay);
		};

		function tick() {
			if (pauseWhenHidden && document.hidden) {
				scheduleNext(1000);
				return;
			}

			const remaining = Math.ceil((deadlineAt.current - Date.now()) / 1000);

			if (remaining > 0) {
				setSecondsLeft(remaining);
				scheduleNext();
				return;
			}

			deadlineAt.current = Date.now() + intervalSec * 1000;
			setSecondsLeft(intervalSec);
			scheduleNext();
			onRefreshRef.current();
		}

		// Návrat do aplikace: hodnotu srovnat okamžitě a odtud znovu chytit fázi sekund.
		const syncCountdown = () => {
			tick();
		};

		rescheduleRef.current = () => scheduleNext();

		if (refreshOnStart) {
			onRefreshRef.current();
		}

		scheduleNext();
		document.addEventListener('visibilitychange', syncCountdown);
		window.addEventListener('focus', syncCountdown);
		window.addEventListener('pageshow', syncCountdown);

		return () => {
			rescheduleRef.current = null;

			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}

			document.removeEventListener('visibilitychange', syncCountdown);
			window.removeEventListener('focus', syncCountdown);
			window.removeEventListener('pageshow', syncCountdown);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, intervalSec, pauseWhenHidden, refreshOnStart]);

	return {secondsLeft, restart};
}
